import { useEffect, useState } from "react";
import { Aviso, Botao, Campo, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { useFusao, type TipoDeImportacao } from "../../nucleo/cadastro";

function normalizar(texto: string): string {
  return texto.trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");
}

/**
 * A fusão de duas duplicadas, em dois tempos: primeiro a prévia (o que vai ser movido, com os
 * avisos do que muda), depois a confirmação. Nada é gravado até o clique em "Fundir agora" — e,
 * no servidor, a fusão inteira vale ou nada vale.
 */
export function FusaoEmCurso({
  tipo,
  destino,
  origem,
  aoConcluir,
  aoCancelar,
}: {
  tipo: TipoDeImportacao;
  destino: { id: string; nome: string };
  origem: { id: string; nome: string };
  aoConcluir?: () => void;
  aoCancelar: () => void;
}) {
  const { previa, fundir } = useFusao(tipo);
  const { mutate: pedirPrevia } = previa;
  const [digitado, setDigitado] = useState("");

  useEffect(() => {
    pedirPrevia({ destinoId: destino.id, origemId: origem.id });
  }, [pedirPrevia, destino.id, origem.id]);

  const coisa = tipo === "organizacoes" ? "organização" : "pessoa";
  // Quando as empresas não parecem a mesma (CNPJs sem a mesma raiz), a fusão só libera depois de a
  // pessoa digitar o nome da absorvida — o servidor confere de novo.
  const exigeNome = previa.data?.exige_confirmacao_digitada ?? false;
  const nomeDaAbsorvida = previa.data?.origem_nome ?? origem.nome;
  const nomeConfere = normalizar(digitado) === normalizar(nomeDaAbsorvida) && digitado.trim() !== "";

  if (fundir.isSuccess) {
    return (
      <Aviso tom="sucesso" data-testid="fusao-concluida">
        <b>{origem.nome}</b> foi fundida em <b>{destino.nome}</b>: {fundir.data.total} registro(s)
        movido(s).
        {fundir.data.ajustes.length > 0 ? (
          <ul className="mt-2">
            {fundir.data.ajustes.map((ajuste) => (
              <li key={ajuste}>{ajuste}</li>
            ))}
          </ul>
        ) : null}
        <div className="mt-3">
          <Botao tamanho="pequeno" variante="fantasma" onClick={aoConcluir ?? aoCancelar}>
            Fechar
          </Botao>
        </div>
      </Aviso>
    );
  }

  return (
    <Cartao
      titulo={`Fundir “${origem.nome}” em “${destino.nome}”`}
      data-testid="fusao-em-curso"
      className="mt-3"
    >
      {previa.isPending ? <p role="status">Calculando o que será movido…</p> : null}
      {previa.isError ? <Aviso tom="erro">{mensagemDoConsole(previa.error)}</Aviso> : null}
      {previa.data ? (
        <>
          <p className="mb-2">
            {previa.data.total === 0
              ? `Não há nada vinculado a ${origem.nome} para mover.`
              : `Vão para ${destino.nome}:`}
          </p>
          {previa.data.itens.length > 0 ? (
            <ul className="mb-3" data-testid="itens-da-fusao">
              {previa.data.itens.map((item) => (
                <li key={item.tabela}>
                  {item.rotulo}: <b>{item.quantidade}</b>
                </li>
              ))}
            </ul>
          ) : null}
          {previa.data.avisos.map((aviso) => (
            <Aviso key={aviso} tom="atencao" className="mb-2">
              {aviso}
            </Aviso>
          ))}
          {exigeNome ? (
            <Campo
              rotulo={`Digite “${nomeDaAbsorvida}” para confirmar`}
              value={digitado}
              onChange={(e) => setDigitado(e.target.value)}
              autoComplete="off"
              data-testid="nome-da-absorvida"
            />
          ) : null}
          <p className="nota-rodape mb-3">
            A fusão é registrada na auditoria e não tem volta pela tela: confira os dois cadastros
            antes de confirmar.
          </p>
        </>
      ) : null}
      {fundir.isError ? <Aviso tom="erro">{mensagemDoConsole(fundir.error)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao
          onClick={() =>
            previa.data &&
            fundir.mutate({
              destinoId: destino.id,
              origemId: origem.id,
              token: previa.data.token_de_confirmacao,
              ...(exigeNome ? { confirmacaoDigitada: digitado } : {}),
            })
          }
          disabled={!previa.data || fundir.isPending || (exigeNome && !nomeConfere)}
          data-testid="confirmar-fusao"
        >
          {fundir.isPending ? "Fundindo…" : `Fundir ${coisa} agora`}
        </Botao>
        <Botao variante="fantasma" onClick={aoCancelar} disabled={fundir.isPending}>
          Cancelar
        </Botao>
      </div>
    </Cartao>
  );
}
